import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { SelectCollectingContext } from "../SelectContext";

export interface SelectItemCollectorProps {
  children?: ReactNode;
}

/**
 * Renders `Select.Content`'s children into a detached fragment while the list isn't showing them
 * (closed, or hidden by a loading state), so every `Select.Item` stays mounted and registered — that
 * is how the trigger shows the selected label before first open, and how the item count is known.
 * Items render nothing here; the fragment is never attached, so
 * any other markup stays out of the page. Once the list mounts, it takes over rendering the children
 * and this unmounts, so items never mount twice.
 *
 * This is the same technique Radix's own Select uses: a closed `Select.Content` portals its children
 * into a `DocumentFragment` to keep its item collection populated (`@radix-ui/react-select`,
 * `SelectContent`).
 */
export const SelectItemCollector = ({ children }: SelectItemCollectorProps) => {
  const [fragment] = useState(() => document.createDocumentFragment());

  return createPortal(<SelectCollectingContext.Provider value>{children}</SelectCollectingContext.Provider>, fragment);
};
